import { CheckCircle2, ClipboardList, GlassWater, Percent, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useBeverageDashboard } from '../../hooks/useBeverageAuthorizations';
import { CATEGORICAL_CHART_COLORS, STATUS_CHART_COLORS } from '../../lib/chartPalette';
import type { BeverageDashboardCounts } from '../../types/api';
import MetricCard from '../MetricCard';

type Summary = BeverageDashboardCounts;

/** KPI card row — shared by the module dashboard page and the main-dashboard section. */
export default function BeverageSummaryCards({ summary }: { summary: Summary }) {
  return (
    <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'>
      <MetricCard title='Total requests' value={summary.total} icon={<GlassWater className='h-4 w-4' />} color='blue' />
      <MetricCard title='Approved' value={summary.approved} icon={<CheckCircle2 className='h-4 w-4' />} color='green' />
      <MetricCard
        title='Correction required'
        value={summary.correctionRequired + summary.followUpRequired}
        icon={<ClipboardList className='h-4 w-4' />}
        color='yellow'
      />
      <MetricCard title='Denied' value={summary.denied} icon={<XCircle className='h-4 w-4' />} color='red' />
      <MetricCard
        title='Pending'
        value={summary.submitted + summary.received + summary.pendingAssessment}
        icon={<ClipboardList className='h-4 w-4' />}
        color='purple'
      />
      <MetricCard
        title='Compliance rate'
        value={summary.complianceRate !== null ? `${summary.complianceRate}%` : '—'}
        icon={<Percent className='h-4 w-4' />}
        color='green'
      />
    </div>
  );
}

/** Self-contained analytics section rendered inside the main Dashboard's "Beverages" tab. */
export function BeverageAnalyticsPanel() {
  const { data, isLoading } = useBeverageDashboard({ groupBy: 'beverageType' });

  if (isLoading || !data) {
    return (
      <div className='p-8 text-center text-sm' style={{ color: 'var(--nhip-ink-muted)' }}>
        Loading beverage analytics…
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>
          Traditional Beverage Authorizations
        </h2>
        <Link to='/beverage-authorizations/dashboard' className='text-xs font-medium text-primary hover:underline'>
          Open full dashboard →
        </Link>
      </div>

      <BeverageSummaryCards summary={data.summary} />

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <div className='attio-card p-4'>
          <h3 className='text-xs font-semibold mb-2' style={{ color: 'var(--nhip-ink)' }}>
            Monthly requests
          </h3>
          <ResponsiveContainer width='100%' height={220}>
            <LineChart data={data.trends}>
              <CartesianGrid strokeDasharray='3 3' stroke='var(--nhip-border)' />
              <XAxis dataKey='month' tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line
                type='monotone'
                dataKey='total'
                name='Requests'
                stroke={CATEGORICAL_CHART_COLORS[0]}
                strokeWidth={2}
                dot={false}
              />
              <Line
                type='monotone'
                dataKey='approved'
                name='Approved'
                stroke={STATUS_CHART_COLORS.good}
                strokeWidth={2}
                dot={false}
              />
              <Line
                type='monotone'
                dataKey='denied'
                name='Denied'
                stroke={STATUS_CHART_COLORS.critical}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className='attio-card p-4'>
          <h3 className='text-xs font-semibold mb-2' style={{ color: 'var(--nhip-ink)' }}>
            Requests by beverage type
          </h3>
          <ResponsiveContainer width='100%' height={220}>
            <BarChart data={data.breakdowns ?? []} layout='vertical'>
              <CartesianGrid strokeDasharray='3 3' stroke='var(--nhip-border)' />
              <XAxis type='number' allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type='category' dataKey='label' width={110} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey='count' name='Requests' fill={CATEGORICAL_CHART_COLORS[0]} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className='attio-card p-4 overflow-x-auto'>
        <h3 className='text-xs font-semibold mb-2' style={{ color: 'var(--nhip-ink)' }}>
          Top villages by requests
        </h3>
        <table className='attio-table w-full text-xs'>
          <thead>
            <tr>
              <th className='text-left px-3 py-1.5'>Village</th>
              <th className='text-right px-3 py-1.5'>Requests</th>
              <th className='text-right px-3 py-1.5'>Denied</th>
              <th className='text-right px-3 py-1.5'>Denial rate</th>
            </tr>
          </thead>
          <tbody>
            {data.topVillages.map(village => (
              <tr key={village.villageId}>
                <td className='px-3 py-1.5'>{village.villageName}</td>
                <td className='px-3 py-1.5 text-right'>{village.total}</td>
                <td className='px-3 py-1.5 text-right'>{village.denied}</td>
                <td className='px-3 py-1.5 text-right'>{village.denialRate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
